import { useEffect, useState } from 'react';

import { swallow } from '../utils/function';

type Props = {
  wrapper: Nullable<string>;
  targets?: Nullable<string[]>;
  config?: { rootMargin?: string; threshold?: number };
};

/**
 * Hook observant la visibilité des éléments spécifiés dans un élément conteneur.
 * - Le conteneur est défini à partir de la valeur de l'attribut `data-observer`.
 * - Les éléments cibles sont les enfants possédant l'attribut `data-observed`.
 *
 * Si une liste de valeurs est passée pour l'attribut `targets`, la surveillance
 * se limitera aux enfants du container dont l'attribut `data-observed` correspond.
 *
 * ```tsx
 * const simpleValues = useElementsObserver({ wrapper: 'my-wrapper' });
 * const filterValues = useElementsObserver({ wrapper: 'my-wrapper', targets: ['1', '2'] });
 *
 * return (
 *   <div data-observer="my-wrapper">
 *     <div data-observed="1">
 *        <div>Simple: {simpleValues.includes('1') ? 'Yes' : 'No'}</div>
 *        <div>Filter: {filterValues.includes('1') ? 'Yes' : 'No'}</div>
 *     </div>
 *     <div data-observed="2">
 *        <div>Simple: {simpleValues.includes('2') ? 'Yes' : 'No'}</div>
 *        <div>Filter: {filterValues.includes('2') ? 'Yes' : 'No'}</div>
 *     </div>
 *     <div data-observed="3">
 *        <div>Simple: {simpleValues.includes('3') ? 'Yes' : 'No'}</div>
 *        <div>Filter: {filterValues.includes('3') ? 'Yes' : 'No'}</div> // Toujours "No"
 *     </div>
 *   </div>
 * );
 * ```
 */
export const useElementsObserver = (args: Props) => {
  const { wrapper, targets, config } = args;
  const [observed, setObserved] = useState<string[]>([]);

  // Extraction des options pour l'observer
  const threshold = config?.threshold ?? 0;
  const rootMargin = config?.rootMargin ?? '0px 0px 0px 0px';

  // Création des sélecteurs css pour le contenant et les cibles
  const sWrapper = wrapper ? `[data-observer="${wrapper}"]` : null;
  const sTargets = targets?.map(v => `[data-observed="${v}"]`).join(',') ?? '[data-observed]';

  useEffect(() => {
    // Récupération du contenant et des cibles
    if (!sWrapper || !sTargets) return () => null;
    const nWrapper = swallow(() => document.querySelector(sWrapper));
    const nTargets = swallow(() => nWrapper?.querySelectorAll(sTargets));

    // Pour stocker la visbilité des éléments surveillés
    const values = new Set<string>();

    // Mise à jour de la map et du state à chaque changement
    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(({ target, isIntersecting }) => {
        const key = target.getAttribute('data-observed');
        if (key) values[isIntersecting ? 'add' : 'delete'](key);
      });
      setObserved([...values]);
    };

    // Surveillance des éléments
    const observer = new IntersectionObserver(callback, { threshold, rootMargin });
    nTargets?.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [sWrapper, sTargets, threshold, rootMargin]);

  return observed;
};
